import React, { Fragment, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "../../Context/AuthContext";
import { submitFeedback, dismissFeedback } from "./feedbackApi";
import styles from "./FeedbackFaces.module.css";
import { FeedbackFaces } from "./FeedbackFaces";
import { getBrowserDetails } from "../../constants";
import { GlobalContext } from "../../Context/GlobalContext";

// Comment categories. label is also the category sent to the backend.
// Any number of them can be selected at once; each selected one gets its own
// comment box below the chips.
const FEEDBACK_CATEGORIES = [
  {
    label: "Problem",
    icon: "ti ti-alert-triangle",
    placeholder: "Let us know what issue you faced or what could be improved...",
    bg: "#FCEBEB",
    color: "#A32D2D",
  },
  {
    label: "Suggestion",
    icon: "ti ti-bulb",
    placeholder: "What's your idea? How could we make this better...",
    bg: "#FAEEDA",
    color: "#854F0B",
  },
  {
    label: "Compliment",
    icon: "ti ti-heart",
    placeholder: "Tell us what you loved — what worked well for you...",
    bg: "#EAF7E5",
    color: "#1E6B1E",
  },
  {
    label: "Products",
    icon: "ti ti-package",
    placeholder: "Any thoughts about our products or something you'd like to see...",
    bg: "#E6F1FB",
    color: "#185FA5",
  },
  {
    label: "Others",
    icon: "ti ti-dots",
    placeholder: "Anything else you'd like to share...",
    bg: "#EEEDFE",
    color: "#3C3489",
  },
];

// The catch-all box shown while no category is selected. It is not a chip — it
// disappears (and is cleared) as soon as the user picks a category, so a
// submission is either one general comment or one comment per category.
const GENERAL = {
  label: "General",
  heading: "Comment",
  icon: "ti ti-message-2",
  placeholder: "Anything you'd like to share...",
  bg: "var(--bg-surface)",
  color: "var(--text-secondary)",
  focus: "#555555",
};

const MAX_COMMENT = 500;

const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const FeedbackModalButton = ({ isOpen = false, source = "manual", onClose }) => {
  const { userId, userName, companyId } = useAuth() || {};
  const { OrderId } = useContext(GlobalContext) || {};

  // Suppression (don't-show-again + mark-submitted) applies ONLY to the automatic
  // checkout popup on the BillInfo page. Opening via the manual Feedback button must
  // never write suppression state or hide the button next time.
  const isCheckout = source === "checkout";

  const [rating, setRating] = useState(0);
  // Labels of the chips the user has pressed, in the order they pressed them.
  const [selected, setSelected] = useState([]);
  // Comment text keyed by category label (plus "General").
  const [comments, setComments] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [err, setErr] = useState("");
  const [closing, setClosing] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState(OrderId);
  // Label of the block that was just opened, so we can scroll it into view once.
  const [lastOpened, setLastOpened] = useState(null);
  const blockRefs = useRef({});

  useEffect(() => {
    if (OrderId && OrderId > 0) setCurrentOrderId(OrderId);
  }, [OrderId]);

  useEffect(() => {
    if (!lastOpened) return;
    const el = blockRefs.current[lastOpened];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [lastOpened]);

  const setComment = (key, value) => {
    setComments((prev) => ({ ...prev, [key]: value }));
  };

  const toggleCategory = (label) => {
    const wasOn = selected.includes(label);
    const next = wasOn ? selected.filter((l) => l !== label) : [...selected, label];
    setSelected(next);
    setLastOpened(wasOn ? null : label);
    setComments((prev) => {
      const updated = { ...prev };
      // Un-picking a category throws its text away, so it cannot be submitted invisibly.
      if (wasOn) delete updated[label];
      // The general box is only in play while nothing is picked.
      if (next.length) delete updated[GENERAL.label];
      return updated;
    });
  };

  const close = () => {
    if (closing) return;
    if (isCheckout && !success) dismissFeedback(dontShowAgain);
    setClosing(true);
    setTimeout(() => {
      if (onClose) onClose();
      setRating(0);
      setSelected([]);
      setComments({});
      setLastOpened(null);
      setSuccess(false);
      setErr("");
      setClosing(false);
      setDontShowAgain(false);
    }, 320);
  };

  const handleSubmit = async () => {
    if (rating < 1) {
      setErr("Please select a rating first.");
      return;
    }
    // Either the picked categories that were actually typed into, or the single
    // general comment when no category was picked.
    const feedbackItems = selected.length
      ? FEEDBACK_CATEGORIES.filter((c) => selected.includes(c.label))
          .map((c) => ({ category: c.label, comment: (comments[c.label] || "").trim() }))
          .filter((item) => item.comment.length > 0)
      : [{ category: GENERAL.label, comment: (comments[GENERAL.label] || "").trim() }].filter(
          (item) => item.comment.length > 0,
        );

    setErr("");
    setSubmitting(true);
    let browserSource = getBrowserDetails();
    const ok = await submitFeedback({
      userId: String(userId || sessionStorage.getItem("UserID") || ""),
      userName: userName || "",
      companyId: companyId ? String(companyId) : "",
      orderId: currentOrderId ? String(currentOrderId) : "",
      pageUrl: window.location.pathname,
      rating,
      ratingStyle: "faces",
      feedbackItems,
      source: JSON.stringify(browserSource),
      // Only a checkout-popup submit suppresses the automatic BillInfo popup.
      markSubmitted: isCheckout,
    });
    setSubmitting(false);
    if (ok) {
      // Suppression is now recorded server-side by SubmitFeedback (SUBMITTED key).
      setSuccess(true);
      setTimeout(() => close(), 2200);
    } else {
      setSuccess(true);
      setErr("Could not submit your feedback. Please try again.");
    }
  };

  if (!isOpen) return null;

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget && !submitting) close();
  };

  // A comment box plus its coloured header. `cfg` is a category or GENERAL.
  const renderBlock = (cfg) => {
    const value = comments[cfg.label] || "";
    const focusColor = cfg.focus || cfg.color;
    return (
      <div
        key={cfg.label}
        className={styles.commentBlock}
        ref={(el) => {
          blockRefs.current[cfg.label] = el;
        }}
      >
        <div className={styles.blockHeader}>
          <span className={styles.blockLabel} style={{ backgroundColor: cfg.bg, color: cfg.color }}>
            <i className={cfg.icon} aria-hidden="true" />
            {cfg.heading || cfg.label}
          </span>
          <span className={styles.charCount}>
            {value.length} / {MAX_COMMENT}
          </span>
        </div>
        <textarea
          className={styles.commentBox}
          maxLength={MAX_COMMENT}
          placeholder={cfg.placeholder}
          value={value}
          onChange={(e) => setComment(cfg.label, e.target.value)}
          aria-label={`${cfg.heading || cfg.label} comment`}
          style={{
            "--focus-color": focusColor,
            "--focus-ring": focusColor.startsWith("#") ? hexToRgba(focusColor, 0.12) : "rgba(85, 85, 85, 0.12)",
          }}
          autoFocus
        />
      </div>
    );
  };

  return (
    <div
      className={`${styles.fbVars} ${styles.fbBackdrop} ${closing ? styles.fbBackdropClosing : ""}`}
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
    >
      <div className={`${styles.fbDialog} ${closing ? styles.fbDialogClosing : ""}`}>
        {!submitting && (
          <button type="button" className={styles.fbClose} aria-label="Close" onClick={close}>
            &times;
          </button>
        )}

        {success ? (
          <div className={styles.fbSuccess}>
            <svg className={styles.checkmark} viewBox="0 0 60 60">
              <circle cx={30} cy={30} r={30} fill="#2FAE2F" />
              <path className={styles.tick} d="M18 31 L26 39 L42 22" />
            </svg>
            <p className={styles.fbSuccessText}>Thank you for your feedback!</p>
          </div>
        ) : (
          <Fragment>
            <h4 className={styles.fbHeading}>How was your experience on our website?</h4>
            <p className={styles.fbSub}>
              {source === "checkout"
                ? "How was your ordering experience?"
                : "Select the option that best reflects how you feel"}
            </p>

            <FeedbackFaces value={rating} onChange={setRating} />

            <hr className={styles.divider} />

            <p className={styles.commentHeading}>Your comments (optional)</p>
            <p className={styles.commentSub}>
              Leave a general comment below, or pick one or more categories to leave specific feedback — a problem, a
              compliment, an idea, or all three.
            </p>

            <div className={styles.categoriesRow} role="group" aria-label="Feedback categories">
              {FEEDBACK_CATEGORIES.map((cat) => {
                const isOn = selected.includes(cat.label);
                return (
                  <button
                    key={cat.label}
                    type="button"
                    aria-pressed={isOn}
                    className={`${styles.categoryChip} ${isOn ? styles.categoryChipOn : ""}`}
                    style={isOn ? { "--chip-bg": cat.bg, "--chip-color": cat.color } : undefined}
                    onClick={() => toggleCategory(cat.label)}
                  >
                    <i className={`${cat.icon} ${styles.chipIcon}`} aria-hidden="true" />
                    <span>{cat.label}</span>
                    <i className={`ti ti-check ${styles.chipCheck}`} aria-hidden="true" />
                  </button>
                );
              })}
            </div>

            <div className={styles.commentBlocks}>
              {selected.length
                ? FEEDBACK_CATEGORIES.filter((cat) => selected.includes(cat.label)).map(renderBlock)
                : renderBlock(GENERAL)}
            </div>

            {err && <div className={styles.fbError}>{err}</div>}

            {isCheckout && (
              <label className={styles.dontShowRow}>
                <input
                  type="checkbox"
                  className={styles.dontShowCheckbox}
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                  disabled={submitting}
                />
                <span>Don't show me this again</span>
              </label>
            )}

            <div className={styles.actionRow}>
              {dontShowAgain ? (
                <button type="button" className={styles.submitBtn} onClick={close} disabled={submitting}>
                  Submit
                </button>
              ) : (
                <Fragment>
                  <button type="button" className={styles.notNowBtn} onClick={close} disabled={submitting}>
                    {isCheckout ? "Show Me Later" : "Not now"}
                  </button>
                  <button type="button" className={styles.submitBtn} onClick={handleSubmit} disabled={submitting}>
                    {submitting ? "Submitting..." : "Submit feedback"}
                  </button>
                </Fragment>
              )}
            </div>
          </Fragment>
        )}
      </div>
    </div>
  );
};

export default FeedbackModalButton;
